import React, { useState, useEffect } from 'react';
import apiFetch from '../../../../utils/api';
import Preloader from '../../../../components/Preloader/Preloader';
import Pagination from '../../../../components/Pagination/Pagination';
import './AdminStyles.css';

const AdminUserRewards = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, pending: 0, paid: 0 });
  const [acting, setActing] = useState(null);
  const [activeTab, setActiveTab] = useState(''); // '' = All, 'Pending', 'Paid'

  const fetchData = () => {
    setLoading(true);
    let url = `/api/admin/user-rewards?page=${page}`;
    if (activeTab) url += `&status=${activeTab}`;

    apiFetch(url)
      .then(res => res.json())
      .then(res => { 
        setData(res.claims || []); 
        setTotalPages(res.totalPages || 1);
        setStats(res.stats || { total: 0, pending: 0, paid: 0 });
        setLoading(false); 
      })
      .catch(() => setLoading(false));
  };

  useEffect(fetchData, [page, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  const handleAction = async (id, action) => {
    setActing(id);
    try {
      const res = await apiFetch(`/api/admin/user-rewards/${id}/action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      const d = await res.json();
      if (d.success) fetchData();
    } catch {}
    setActing(null);
  };

  return (
    <div className="admin-page">
      <div className="admin-breadcrumb">🏠 / Admin / User Rewards</div>
      <h2 className="admin-title">User Reward Claims</h2>

      <div className="admin-stats-bar">
        <div className="admin-stat-chip blue">🏆 {stats.total} Total Claims</div>
        <div className="admin-stat-chip orange">⏳ {stats.pending} Pending Payment</div>
        <div className="admin-stat-chip green">✅ {stats.paid} Paid</div>
      </div>
      
      {/* Tab Buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => handleTabChange('')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: activeTab === '' ? 'var(--awp-accent-gradient)' : 'var(--awp-surface-2)',
            color: activeTab === '' ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
          }}
        >
          🏆 All Claims
        </button>
        <button
          onClick={() => handleTabChange('Pending')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: activeTab === 'Pending' ? 'var(--awp-accent-gradient)' : 'var(--awp-surface-2)',
            color: activeTab === 'Pending' ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
          }}
        >
          ⏳ Pending Payment
        </button>
        <button
          onClick={() => handleTabChange('Paid')}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
            background: activeTab === 'Paid' ? 'var(--awp-success-gradient)' : 'var(--awp-surface-2)',
            color: activeTab === 'Paid' ? 'var(--awp-accent-contrast)' : 'var(--awp-text-muted)',
          }}
        >
          ✅ Paid Rewards
        </button>
      </div>

      {stats.pending > 0 && activeTab !== 'Paid' && (
        <div style={{ 
          padding: '12px 16px', background: 'var(--awp-warning-soft)', 
          borderRadius: 10, marginBottom: 20, border: '1px solid var(--awp-warning-border)',
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <span style={{ fontSize: 20 }}>🔔</span>
          <div>
            <strong style={{ color: 'var(--awp-warning)' }}>{stats.pending} reward claim{stats.pending > 1 ? 's' : ''} need processing!</strong>
            <div style={{ fontSize: 12, color: 'var(--awp-warning)', marginTop: 2 }}>
              Clicking "Mark Paid" will <strong>automatically credit the user's available balance</strong> and record the transaction.
            </div>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          {activeTab === 'Pending' ? 'Pending Payment Requests' : activeTab === 'Paid' ? 'Paid Rewards' : 'All Reward Claims'}
        </div>
        <div className="admin-card-body">
          {loading ? (
            <Preloader message="Fetching user reward claims..." fullScreen={false} />
          ) : data.length === 0 ? (
            <div className="admin-empty">No reward claims yet. Users can claim rewards from the Alpha Bonuses page.</div>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Reward Level</th>
                      <th>Reward Amount</th>
                      <th>Team (Sponsor)</th>
                      <th>Total Earned</th>
                      <th>Payment Status</th>
                      <th>Claimed Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map(row => (
                      <tr key={row.id}>
                        <td>{row.userId}</td>
                        <td><strong>{row.username}</strong></td>
                        <td>{row.email}</td>
                        <td>
                          <span style={{ 
                            background: 'var(--awp-accent-gradient, linear-gradient(135deg, var(--awp-accent), var(--awp-accent-hover)))', 
                            color: 'var(--awp-accent-contrast)', padding: '4px 12px', borderRadius: 20, 
                            fontSize: 12, fontWeight: 600 
                          }}>
                            {row.rewardRank}
                          </span>
                        </td>
                        <td><span className="user-balance-chip">Rs. {row.rewardAmount}</span></td>
                        <td>{row.team || <span style={{ color: 'var(--awp-text-subtle)' }}>—</span>}</td>
                        <td>Rs. {row.totalEarned}</td>
                        <td>
                          <span className={`status-badge ${row.paymentStatus === 'Paid' ? 'approved' : 'pending'}`}>
                            {row.paymentStatus === 'Paid' ? '✅ Paid' : '⏳ Pending'}
                          </span>
                        </td>
                        <td>{row.claimedAt}</td>
                        <td>
                          <div className="admin-actions-row">
                            {row.paymentStatus === 'Pending' ? (
                              <button 
                                className="admin-btn admin-btn--approve" 
                                disabled={acting === row.id} 
                                onClick={() => handleAction(row.id, 'paid')}
                              >
                                {acting === row.id ? '...' : '✅ Mark Paid'}
                              </button>
                            ) : (
                              <span style={{ color: 'var(--awp-text-muted)', fontSize: 12, fontStyle: 'italic' }}>
                                ✅ Fully Processed
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination 
                currentPage={page} 
                totalPages={totalPages} 
                onPageChange={setPage} 
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserRewards;
